import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../constants";
import {
  getWidgetLinkId,
  probeLinkSession,
} from "../services/api";
import { resolveXLinkIdHeader } from "../services/resolveXLinkIdHeader";
import { useSessionStore } from "../store/useSessionStore";
import { RequestStatus } from "../utils/commons";
import { shouldSkipLinkLivenessProbe } from "../utils/linkLivenessProbe";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";

const DEAD_LINK_PATHS = [
  APP_ROUTES.SESSION_EXPIRED_PAGE,
  APP_ROUTES.ERROR_PAGE,
  APP_ROUTES.MAINTENANCE_PAGE,
] as const;

function resolveProbeLinkId(): string | null {
  return resolveXLinkIdHeader({
    urlLinkId: resolveUrlLinkId(),
    currentLinkId: getWidgetLinkId(),
    persistedLinkId: useSessionStore.getState().session.data?.linkId ?? null,
  });
}

/**
 * On tab resume / BFCache restore, silently GET the link session. Live = no-op.
 * Dead = interceptor + `useSessionExpiredHandler`. Network = fail-open.
 */
export function useTabResumeLinkLiveness() {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const probeInFlightRef = useRef(false);
  const lastAttemptAtRef = useRef<number | null>(null);
  const wasHiddenRef = useRef(false);

  useEffect(() => {
    const runProbe = () => {
      const linkId = resolveProbeLinkId();
      const skip = shouldSkipLinkLivenessProbe({
        linkId,
        pathname: pathnameRef.current,
        deadLinkPaths: DEAD_LINK_PATHS,
        claimInFlight:
          useSessionStore.getState().session.status === RequestStatus.LOADING,
        probeInFlight: probeInFlightRef.current,
        lastAttemptAt: lastAttemptAtRef.current,
        now: Date.now(),
      });
      if (skip || !linkId) return;

      lastAttemptAtRef.current = Date.now();
      probeInFlightRef.current = true;
      void probeLinkSession(linkId).catch(() => {
        // Fail-open: interceptor already routed dead links.
      }).finally(() => {
        probeInFlightRef.current = false;
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        wasHiddenRef.current = true;
        return;
      }
      if (document.visibilityState === "visible" && wasHiddenRef.current) {
        runProbe();
      }
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        runProbe();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);
}
